// src/Components/Assets/AssetList.js

import React, { useState } from 'react';
import { Modal, Input, Button } from 'antd';
import { PlayCircleOutlined, DownloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { formatBytes } from '../../utils/formatters';

const AssetList = ({ assets, onDelete, onRenameRequest, onSelect, selectedAssets, onPreview }) => {
  if (!assets || assets.length === 0) {
    return (
       <div className="flex flex-col items-center justify-center h-64 bg-white rounded-lg shadow-sm border text-gray-500">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-lg font-medium">Your Media Library is Empty</p>
        <p className="text-sm">Click the "Upload" button to add your first asset.</p>
      </div>
    );
  }

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      onSelect(assets.map(a => a.url)); // Pass all URLs to select all
    } else {
      onSelect([]); // Pass empty array to deselect all
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead className="bg-gray-50 text-xs text-gray-700 uppercase">
          <tr>
            <th className="p-4 w-12">
                {/* --- THIS IS THE FIX: Added "Select All" checkbox --- */}
                <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer accent-brand-primary border-gray-300 rounded focus:ring-brand-primary"
                    checked={assets.length > 0 && selectedAssets.length === assets.length}
                    onChange={handleSelectAll}
                />
                {/* --- END OF FIX --- */}
            </th>
            <th className="px-6 py-3">File Name</th>
            <th className="px-6 py-3">Type</th>
            <th className="px-6 py-3">Size</th>
            <th className="px-6 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {assets.map((asset) => (
            <AssetRow 
                key={asset.url} 
                asset={asset} 
                onDelete={onDelete} 
                onRenameRequest={onRenameRequest} 
                onSelect={onSelect}
                isSelected={selectedAssets.includes(asset.url)}
                onPreview={onPreview}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AssetRow = ({ asset, onDelete, onRenameRequest, onSelect, isSelected, onPreview }) => {
  const isVideo = asset.name?.match(/\.(mp4|mov|webm)$/i);
  // --- THIS IS THE FIX: Added a check for document types ---
  const isDocument = asset.name?.match(/\.(pdf|docx|pptx)$/i);
  const fileType = isVideo ? 'Video' : (isDocument ? 'Document' : 'Image');
  // --- END OF FIX ---
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [newAssetName, setNewAssetName] = useState('');

  const handleRename = () => {
    onRenameRequest(asset.url, newAssetName);
    setIsRenameModalVisible(false);
  };

  return (
    <>
      <tr className={`bg-white border-b hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
        <td className="p-4 w-12">
            <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer accent-brand-primary border-gray-300 rounded focus:ring-brand-primary"
                checked={isSelected}
                onChange={() => onSelect(asset.url)}
            />
        </td>
        <td 
            className="px-6 py-4 font-medium text-gray-900 flex items-center gap-3 whitespace-nowrap cursor-pointer"
            onClick={() => onPreview(asset)}
        >
          {/* --- THIS IS THE FIX: Added dedicated UI for documents --- */}
          {isDocument ? (
            <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                <i className="fa-solid fa-file-alt text-xl text-gray-500"></i>
            </div>
          ) : isVideo ? (
            <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                <PlayCircleOutlined className="text-xl text-gray-500"/>
            </div>
          ) : (
              <img src={asset.url} alt={asset.name} className="w-10 h-10 object-cover rounded flex-shrink-0" />
          )}
          {/* --- END OF FIX --- */}
          <span title={asset.name} className="truncate max-w-xs">{asset.name}</span>
        </td>
        <td className="px-6 py-4">{fileType}</td>
        <td className="px-6 py-4">{formatBytes(asset.size)}</td>
        <td className="px-6 py-4 text-right">
          <div className="flex items-center justify-end gap-2">
            <Button icon={<DownloadOutlined />} href={asset.url} target="_blank" download={asset.name} onClick={(e) => e.stopPropagation()} />
            <Button icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); onRenameRequest(asset); }} />
            <Button icon={<DeleteOutlined />} danger onClick={(e) => { e.stopPropagation(); onDelete(asset.url); }} />
          </div>
        </td>
      </tr>
      <Modal
        title="Rename Asset"
        open={isRenameModalVisible}
        onOk={handleRename}
        onCancel={() => setIsRenameModalVisible(false)}
        okText="Rename"
        destroyOnClose
      >
        <p className="text-sm text-gray-500 mb-2">Enter the new name for the file.</p>
        <Input 
          value={newAssetName}
          onChange={(e) => setNewAssetName(e.target.value)}
          placeholder="Enter new asset name"
          onPressEnter={handleRename}
        />
      </Modal>
    </>
  );
};

export default AssetList;